<!--
  StorefrontCategoryNav.vue -- P4 storefront chrome (persistent category nav).

  Card: Live Storefront - Home/Category/Product (ADR 0060/0061)
  https://trello.com/c/q7EQ3a0b
  Pebble: P4 -- Shared chrome: CategoryNav + Breadcrumb (frontend).

  A pure, presentational, dependency-free horizontal nav bar rendered below
  the storefront header. It consumes the P1 GET /storefront/<slug>/nav
  response shape VERBATIM:

    nav = [{ name, slug, is_home }, ...]

  The backend pre-orders (home first via is_home, then visible categories in
  nav_order) and pre-filters (hidden categories omitted). This component
  renders the array AS GIVEN -- it never re-sorts or re-filters. P5 wires the
  real /nav data in; this component is greenfield and route-agnostic.

  URL model (Q7 canonical -- matches StoreCategories/CategoryCard.vue
  `storefrontUrl`, so the editor's Copy-link URLs resolve):
    Home:     /shop/<storeSlug>
    Category: /shop/<storeSlug>/c/<slug>

  Active state: the item whose slug matches `activeSlug` is highlighted in the
  store's brand color (underlined + bold). When `activeSlug` is empty the
  is_home item is active (the shopper is on Home). Brand color is read from the
  existing Vuex getter (`$store.getters.getBrandColor`, the same source
  StorefrontLayout uses) with an optional `brandColor` prop override for
  preview/standalone use.

  HEADER INVERSION (T+L demo, Jake's call): THIS BAR NOW CARRIES THE BRAND
  COLOR. It used to be hardcoded white with the brand color on the topbar
  above it. A distributor's uploaded logo is frequently an opaque JPEG with
  white baked into its pixels (T+L's real one is: CMYK, 2198x514, no alpha,
  ink edge to edge), so it rendered as a white block against the brand-colored
  topbar. No CSS can make a JPEG's white transparent, so the two bands swap:
  the topbar goes white (the logo's own white becomes the correct background)
  and the brand color moves down here.

  CONTRAST GUARD, now inverted with it: `brand_color` is distributor-chosen and
  unconstrained, and it is this bar's BACKGROUND. A constant link ink is
  therefore wrong in one direction or the other -- white links vanish on a
  cream brand, dark links vanish on a navy one. utils/storefrontNavBarTheme.js
  decides the ink from the bar's own luminance and clamps every result to its
  WCAG floor (4.5:1 text, 3:1 non-text underline). A store with no/malformed
  brand_color falls back to the PRE-INVERSION nav (white bar, dark links), so
  the degraded case is a known-good bar rather than a broken one.

  The pre-inversion helper (utils/storefrontNavInk.js) still owns the
  foreground-against-a-fixed-white-bar case and supplies the clampToContrast
  primitive the new module composes -- see that file's header for the two WCAG
  bars and why text and underline differ.

  Test-id convention: `sf-category-nav` on the container; `sf-nav-item-<slug>`
  per item (home uses the stable suffix `home` since its slug is empty); the
  active item also carries `sf-nav-item--active` + `data-active="true"`.
-->
<template>
  <!--
    The bar's background and every item's ink are bound INLINE rather than
    living in the scoped stylesheet: they are per-store data (derived from
    stores.brand_color), and a static stylesheet cannot express "this store's
    color". The scoped <style> below keeps only what is the same for every
    store -- layout, spacing, weight.

    --sf-nav-hover-ink is published as a custom property so the :hover rule in
    the stylesheet can reach the derived value; a hover color cannot be applied
    as an inline style.
  -->
  <nav
    class="sf-category-nav"
    data-testid="sf-category-nav"
    :style="barStyle"
  >
    <router-link
      v-for="item in navItems"
      :key="item.key"
      :to="item.to"
      class="sf-nav-item"
      :class="{ 'sf-nav-item--active': item.active }"
      :data-testid="`sf-nav-item-${item.key}`"
      :data-active="item.active ? 'true' : 'false'"
      :style="item.active ? activeStyle : restingStyle"
    >{{ item.name }}</router-link>
  </nav>
</template>

<script>
import { storefrontNavBarTheme } from "@/utils/storefrontNavBarTheme";

export default {
  name: "StorefrontCategoryNav",

  props: {
    // The P1 /nav payload: home + visible categories, pre-ordered and
    // pre-filtered by the backend. Rendered verbatim (no client sort/filter).
    nav: {
      type: Array,
      default: () => [],
    },
    // The store's own slug -- the nav paths nest under it.
    storeSlug: {
      type: String,
      default: "",
    },
    // The category slug of the page currently being viewed. Empty string (or
    // omitted) means the shopper is on Home, so the is_home item is active.
    activeSlug: {
      type: String,
      default: "",
    },
    // Optional brand-color override for preview / standalone contexts. When
    // absent, the component reads the store's brand color from Vuex (the same
    // source StorefrontLayout uses).
    brandColor: {
      type: String,
      default: "",
    },
  },

  computed: {
    // The brand color that paints this bar. Prop first (preview/standalone),
    // then the Vuex getter StorefrontLayout reads from.
    //
    // NOTE the deliberate absence of a hardcoded default here. It used to end
    // `|| "#133752"`, which was correct when the brand color was only an
    // ACCENT on a white bar -- a missing value just meant a default accent.
    // Now the value is the bar's whole BACKGROUND, and substituting an
    // arbitrary navy would paint an unbranded store in a color it never
    // chose. Passing the empty/absent value straight through lets
    // storefrontNavBarTheme apply its own documented fallback (the
    // pre-inversion white bar with dark links), which is the honest
    // degradation for "this store has no brand color".
    resolvedBrandColor() {
      if (this.brandColor) return this.brandColor;
      return this.$store && this.$store.getters
        ? this.$store.getters.getBrandColor
        : "";
    },

    // Bar background + every ink, all derived from the brand color and each
    // clamped to its WCAG floor. Computed once per brand color and shared by
    // the three style bindings below.
    theme() {
      return storefrontNavBarTheme(this.resolvedBrandColor);
    },

    // The bar itself. --sf-nav-hover-ink is published here (rather than as an
    // inline color) because :hover cannot be expressed inline; the scoped
    // stylesheet's hover rule consumes it.
    barStyle() {
      return {
        background: this.theme.background,
        "--sf-nav-hover-ink": this.theme.linkHover,
      };
    },

    // Resting items: legible against the bar but deliberately quieter than
    // the active one, so the active state still reads as the strongest thing
    // in the nav.
    restingStyle() {
      return { color: this.theme.link };
    },

    // The active item's ink + underline. Held to two DIFFERENT WCAG bars
    // (4.5:1 for the label as text, 3:1 for the underline as a graphical
    // affordance) -- see utils/storefrontNavInk.js for why they differ.
    activeStyle() {
      return {
        color: this.theme.linkActive,
        borderBottomColor: this.theme.activeUnderline,
      };
    },

    // Map the raw /nav payload into render-ready items. Order is preserved
    // exactly as the backend sent it -- no sort, no filter.
    navItems() {
      return (Array.isArray(this.nav) ? this.nav : []).map((entry) => {
        const isHome = !!entry.is_home;
        const slug = entry.slug || "";
        // Home's slug is empty; use a stable "home" testid suffix so it stays
        // addressable regardless of the empty slug.
        const key = isHome ? "home" : slug;
        const to = isHome
          ? `/shop/${this.storeSlug}`
          : `/shop/${this.storeSlug}/c/${slug}`;
        // Active when the slug matches; Home is active when no category slug
        // is selected (activeSlug empty).
        const active = isHome
          ? !this.activeSlug
          : slug === this.activeSlug;
        return { key, name: entry.name, to, active };
      });
    },
  },
};
</script>

<style scoped>
.sf-category-nav {
  /* PINNED under the storefront header (Jake, 2026-10-19: both bars must stay
   * at the top and the links must always be clickable).
   *
   * StorefrontLayout is a fixed-viewport flex column: the header is a flex
   * sibling ABOVE `.storefront-main`, the scroll container, and this bar is
   * the first child INSIDE that scroller. `sticky; top: 0` therefore pins it
   * to the scroller's top edge, which IS the header's bottom edge -- no
   * header-height arithmetic, so a taller header (wrapped store name on a
   * phone, a tall logo) can neither gap nor overlap it.
   *
   * z-index sits above scrolled content (hero, cards, galleries) and below
   * ProductPage's fixed mobile action bar (20) and every Vuetify overlay
   * (menus, dialogs, the help flyover). */
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 28px;
  /* Background is bound INLINE from the store's brand color (header
   * inversion). No `background` here: a value in this rule would be dead
   * weight at best, and would paint the wrong color for one render if the
   * binding ever resolved late. */
  border-bottom: 1px solid var(--tf-border, #e4e9ee);
  /* Horizontal scrolling ONLY. overflow-y must be stated explicitly: per the
   * CSS overflow spec, setting one axis to a non-`visible` value computes the
   * other axis from `visible` to `auto`, so `overflow-x: auto` on its own
   * silently yields `overflow-y: auto` too. The items carry
   * `margin-bottom: -1px` (below), which pushes their underline 1px past this
   * bar's content edge -- enough for that implicit `auto` to raise a vertical
   * scrollbar on the bar. That 1px sits behind this border-bottom and is not
   * visible either way, so clipping it costs nothing. */
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
}

.sf-nav-item {
  padding: 13px 12px;
  /* BODY SIZE, not chrome size (Jake, 2026-08-28: "the Home and category links
   * need a bigger font").
   *
   * This was 13.5px -- a value that sits between the design system's
   * `--tf-size-tab` (13px, "compact tab labels") and `--tf-size-small` (14px),
   * i.e. it was scaled as though this bar were chrome. It is not: this is the
   * storefront's PRIMARY navigation, the shopper's only route between
   * departments, and the most-used control on the page after the products
   * themselves.
   *
   * 16px is `--tf-size-body` on the ingested design scale (design-context
   * snapshot 20260820-164610, colors_and_type.css). The literal is written out
   * rather than referencing the custom property because that scale is NOT in
   * the app's own styles/_tokens.css yet -- it ships only in the design bundle,
   * so a var(--tf-size-body) here would resolve to nothing and silently fall
   * back to the inherited size. The token is named here instead, so the value
   * can be swapped for the property the day the scale lands in _tokens.css.
   *
   * WHY NOT 14 OR 18. 14px (`--tf-size-small`) is a half-step that satisfies
   * "bigger" on a technicality and would not read as a deliberate change.
   * 18px (`--tf-size-body-lg`) starts to crowd a six-item bar at tablet
   * widths, where the overflow-x scroll on the bar would begin hiding
   * categories behind a swipe. */
  font-size: 16px;
  font-weight: 500;
  /* Ink is bound inline per item (resting vs active), derived from the bar's
   * luminance. The var() fallback here only covers the impossible case of a
   * missing binding -- the component always supplies one. */
  color: var(--tf-fg-2, #3f4f5c);
  text-decoration: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  cursor: pointer;
}

/* Hover reaches the derived ink through the custom property the bar
 * publishes -- a hover state cannot be expressed as an inline style, and the
 * old hardcoded near-black would have been invisible on a dark brand bar. */
.sf-nav-item:hover {
  color: var(--sf-nav-hover-ink, var(--tf-fg-1, #1b2a38));
}

/* Active item: bold + brand-color underline. The brand color itself is
   applied inline (per-store theming) so this rule only owns the weight. */
.sf-nav-item--active {
  font-weight: 600;
}

@media (max-width: 600px) {
  .sf-category-nav {
    gap: 4px;
    padding: 0 12px;
  }

  .sf-nav-item {
    padding: 12px 10px;
  }
}
</style>
